import { money } from "./money";
import { fuelCostUsd } from "./fuelDerived";
import type { FlightLeg, Rates } from "./types";

/**
 * Optional model: treat **personal** legs’ **transportation-related fuel dollars** as an
 * additional **Form 720 percentage (7.5%) tax base**, on top of `charter_usd` handled in
 * `calculator.ts`. Use only when your filing position says those costs belong in the % base.
 */
export type PersonalTransport720PctBasis = "off" | "fuel_cost" | "fuel_cost_2x";

export type PersonalTransport720Options = {
  basis: PersonalTransport720PctBasis;
  isPersonalLeg: (leg: FlightLeg) => boolean;
};

/** Match `aggregateByQuarter` calendar keys (`YYYY-Qn`). */
export function quarterKeyFromFlightDate(isoDate: string): string {
  const parts = isoDate.split("-").map((x) => parseInt(x, 10));
  const y = parts[0];
  const m = parts[1];
  if (!y || !m) return "0-Q0";
  const q = Math.floor((m - 1) / 3) + 1;
  return `${y}-Q${q}`;
}

/**
 * Dollar base on a single leg for the **optional** personal % model (not `charter_usd`).
 * Zero when not personal, excluded from 720, basis off, or fuel cost unknown.
 */
export function personalTransportPercentageBaseUsd(
  leg: FlightLeg,
  rates: Rates,
  opts: PersonalTransport720Options
): number {
  if (opts.basis === "off") return 0;
  if (leg.excludeFrom720) return 0;
  if (!opts.isPersonalLeg(leg)) return 0;
  const fc = fuelCostUsd(leg, rates);
  if (fc === null) return 0;
  if (opts.basis === "fuel_cost") return money(fc);
  if (opts.basis === "fuel_cost_2x") return money(fc * 2);
  return 0;
}

/** 7.5% × base for one leg (money-rounded). */
export function personalTransportPercentageTaxForLeg(
  leg: FlightLeg,
  rates: Rates,
  opts: PersonalTransport720Options
): number {
  const b = personalTransportPercentageBaseUsd(leg, rates, opts);
  if (b <= 0) return 0;
  return money(rates.airTransportationPercentage * b);
}

/** Sum over legs (e.g. full worksheet or scoped range). */
export function personalTransportPercentageTaxTotal(
  legs: FlightLeg[],
  rates: Rates,
  opts: PersonalTransport720Options
): number {
  let s = 0;
  for (const leg of legs) {
    s += personalTransportPercentageTaxForLeg(leg, rates, opts);
  }
  return money(s);
}

/** Per calendar quarter, same keys as `aggregateByQuarter`. */
export function personalTransportPercentageTaxByQuarter(
  legs: FlightLeg[],
  rates: Rates,
  opts: PersonalTransport720Options
): Map<string, number> {
  const m = new Map<string, number>();
  for (const leg of legs) {
    const k = quarterKeyFromFlightDate(leg.flightDate);
    const add = personalTransportPercentageTaxForLeg(leg, rates, opts);
    if (add <= 0) continue;
    m.set(k, money((m.get(k) ?? 0) + add));
  }
  return m;
}

export function personalTransport720BasisLabel(basis: PersonalTransport720PctBasis): string {
  if (basis === "fuel_cost") return "fuel cost (personal legs)";
  if (basis === "fuel_cost_2x") return "2× fuel cost (personal legs)";
  return "";
}

/** Legs in the quarter that contribute to the optional personal % tax (fuel priced, personal, not excluded). */
export type PersonalTransportQuarterStats = {
  legsWithBase: number;
  /** Sum of <code>passengerCount</code> on those legs. */
  passengerSumOnThoseLegs: number;
  baseUsd: number;
  taxUsd: number;
};

export function personalTransportQuarterStats(
  legsInQuarter: FlightLeg[],
  rates: Rates,
  opts: PersonalTransport720Options
): PersonalTransportQuarterStats {
  let legsWithBase = 0;
  let passengerSumOnThoseLegs = 0;
  let baseUsd = 0;
  let taxUsd = 0;
  for (const leg of legsInQuarter) {
    const b = personalTransportPercentageBaseUsd(leg, rates, opts);
    if (b <= 0) continue;
    legsWithBase += 1;
    passengerSumOnThoseLegs += Math.max(0, leg.passengerCount);
    baseUsd = money(baseUsd + b);
    taxUsd = money(taxUsd + personalTransportPercentageTaxForLeg(leg, rates, opts));
  }
  return { legsWithBase, passengerSumOnThoseLegs, baseUsd, taxUsd };
}
